import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { money } from '../../lib/money';
import { useNavigate } from 'react-router-dom';
import {
  Badge,
  Button,
  DataTable,
  EmptyState,
  FilterBar,
  PageContainer,
  PageHeader,
  Pagination,
  Spinner,
} from '../../ui/components';
import { Icon } from '../../ui/icons';
import { useProperty } from '../../app/PropertyContext';
import { useT } from '../../i18n';
import { purchaseOrderPath } from '../../lib/slug';
import { buildFilterParams } from '../../lib/filters';
import { suppliersApi } from '../suppliers/suppliersApi';
import { catalogApi } from '../catalog/catalogApi';
import { purchaseOrdersApi, poStatusMeta, PO_STATUSES, deliveryStatusMeta, DELIVERY_STATUSES } from './purchaseOrdersApi';
import '../requisitions/requisitions.css';

const PAGE_SIZE = 25;

// Labels stay in English here; they are translated with t() where rendered.
const SOURCE_OPTIONS = [
  { value: 'requisition', label: 'From requisition' },
  { value: 'direct', label: 'Raised directly' },
];

export default function PurchaseOrdersPage() {
  const navigate = useNavigate();
  const t = useT();
  const { hasPermission, activePropertyId, properties } = useProperty();
  const canCreate = hasPermission('purchaseorders.create');

  const [data, setData] = useState({ items: [], totalCount: 0, page: 1, pageSize: PAGE_SIZE });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [search, setSearch] = useState('');
  const [values, setValues] = useState({});
  const [suppliers, setSuppliers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [page, setPage] = useState(1);
  const debounceRef = useRef(null);

  useEffect(() => {
    let active = true;
    suppliersApi.list({ page: 1, pageSize: 200, sortBy: 'name' })
      .then((res) => { if (active) setSuppliers(res?.items || []); }).catch(() => {});
    catalogApi.listLocations(activePropertyId)
      .then((res) => { if (active) setLocations(res || []); }).catch(() => {});
    return () => { active = false; };
  }, [activePropertyId]);

  const fields = useMemo(() => [
    { id: 'statuses', label: t('Status'), type: 'multiselect', options: PO_STATUSES.map((s) => ({ value: s.value, label: t(s.label) })) },
    { id: 'deliveryStatuses', label: t('Delivery'), type: 'multiselect', options: DELIVERY_STATUSES.map((s) => ({ value: s.value, label: t(s.label) })) },
    { id: 'supplierIds', label: t('Supplier'), type: 'multiselect', searchable: true, options: suppliers.map((s) => ({ value: s.id, label: s.name })) },
    { id: 'locationIds', label: t('Location'), type: 'multiselect', searchable: true, options: locations.map((l) => ({ value: l.id, label: l.name })) },
    { id: 'total', label: t('Total'), type: 'numberrange', minParam: 'totalMin', maxParam: 'totalMax' },
    { id: 'expected', label: t('Expected'), type: 'daterange', fromParam: 'expectedFrom', toParam: 'expectedTo' },
    { id: 'created', label: t('Created'), type: 'daterange', fromParam: 'createdFrom', toParam: 'createdTo' },
    { id: 'source', label: t('Source'), type: 'select', options: SOURCE_OPTIONS.map((o) => ({ ...o, label: t(o.label) })) },
  ], [suppliers, locations, t]);

  const filterParams = useMemo(() => buildFilterParams(fields, values), [fields, values]);
  const hasFilters = search !== '' || Object.keys(filterParams).length > 0;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await purchaseOrdersApi.list({ page, pageSize: PAGE_SIZE, search, ...filterParams });
      setData(res);
    } catch (err) {
      setError(err?.message || t('Failed to load purchase orders.'));
    } finally {
      setLoading(false);
    }
  }, [page, search, filterParams, t]);

  useEffect(() => { load(); }, [load]);

  const onSearchChange = (value) => {
    setSearchText(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => { setPage(1); setSearch(value); }, 300);
  };

  const onFilterChange = (id, value) => {
    setPage(1);
    setValues((prev) => {
      const next = { ...prev };
      if (value === undefined) delete next[id];
      else next[id] = value;
      return next;
    });
  };

  const open = (row) => navigate(purchaseOrderPath(row));

  const propertyName = (id) => properties.find((p) => p.id === id)?.name;

  const columns = [
    {
      key: 'number', header: t('Number'), width: 130,
      render: (p) => <span style={{ fontFamily: 'var(--font-mono)' }}>{p.number}</span>,
    },
    // In "All properties" view, show which property each order belongs to.
    ...(!activePropertyId ? [{
      key: 'property', header: t('Property'), width: 160,
      render: (p) => propertyName(p.propertyId) || <span className="pp-cell-muted">—</span>,
    }] : []),
    {
      key: 'title', header: t('Title'), width: 280,
      render: (p) => (
        <div>
          <div style={{ fontWeight: 600 }}>{p.title}</div>
          <div className="pp-cell-muted" style={{ fontSize: 'var(--text-xs)' }}>{p.supplierName || t('No supplier assigned')}</div>
        </div>
      ),
    },
    {
      key: 'status', header: t('Status'), width: 150,
      render: (p) => { const m = poStatusMeta(p.status); return <Badge tone={m.tone} dot>{t(m.label)}</Badge>; },
    },
    {
      key: 'delivery', header: t('Delivery'), width: 160,
      render: (p) => {
        const m = deliveryStatusMeta(p.deliveryStatus);
        return m ? <Badge tone={m.tone}>{t(m.label)}</Badge> : <span className="pp-cell-muted">—</span>;
      },
    },
    { key: 'lines', header: t('Lines'), width: 80, align: 'right', render: (p) => p.lineCount },
    { key: 'total', header: t('Total'), width: 140, align: 'right', render: (p) => money(p.total) },
    {
      key: 'expected', header: t('Expected'), width: 130,
      render: (p) => (p.expectedDate ? new Date(p.expectedDate).toLocaleDateString() : <span className="pp-cell-muted">—</span>),
    },
    {
      key: 'source', header: t('Source'), width: 120,
      render: (p) => (p.sourceRequisitionNumber ? <span className="pp-cell-muted" style={{ fontFamily: 'var(--font-mono)' }}>{p.sourceRequisitionNumber}</span> : <span className="pp-cell-muted">—</span>),
    },
  ];

  const totalPages = Math.max(1, Math.ceil(data.totalCount / PAGE_SIZE));

  return (
    <PageContainer size="full">
      <PageHeader
        title={t('Purchase Orders')}
        description={t('Commit to suppliers, route for approval and issue orders. Convert approved requisitions into POs.')}
        icon={<Icon.ShoppingCart size={22} />}
        actions={canCreate ? (
          <Button variant="primary" leadingIcon={<Icon.Plus size={16} />} onClick={() => navigate('/purchase-orders/new')}>
            {t('New order')}
          </Button>
        ) : null}
      />

      <FilterBar
        searchPlaceholder={t('Search by number, title or supplier…')}
        searchValue={searchText}
        onSearchChange={onSearchChange}
        fields={fields}
        values={values}
        onChange={onFilterChange}
      />

      {error ? (
        <EmptyState
          icon={<Icon.CircleAlert size={28} />}
          title={t("Couldn't load purchase orders")}
          description={error}
          action={<Button variant="secondary" onClick={load}>{t('Retry')}</Button>}
        />
      ) : loading ? (
        <div style={{ display: 'grid', placeItems: 'center', padding: 'var(--space-16)' }}><Spinner size={26} /></div>
      ) : (
        <>
          <DataTable
            columns={columns}
            data={data.items}
            rowKey={(p) => p.id}
            density="comfortable"
            onRowClick={open}
            emptyState={
              <EmptyState
                icon={<Icon.ShoppingCart size={28} />}
                title={hasFilters ? t('No matching purchase orders') : t('No purchase orders yet')}
                description={hasFilters ? t('Try adjusting your search or filters.') : t('Raise an order directly, or convert an approved requisition into a purchase order.')}
                action={!hasFilters && canCreate ? (
                  <Button variant="primary" leadingIcon={<Icon.Plus size={16} />} onClick={() => navigate('/purchase-orders/new')}>{t('New order')}</Button>
                ) : null}
              />
            }
          />
          {data.totalCount > 0 && (
            <div style={{ marginTop: 'var(--space-4)' }}>
              <Pagination page={page} pageCount={totalPages} onPageChange={setPage} totalItems={data.totalCount} pageSize={PAGE_SIZE} />
            </div>
          )}
        </>
      )}
    </PageContainer>
  );
}
